import { Temporal } from "@js-temporal/polyfill";

/**
 * Converts an ISO date-time string to a localized time string in the given
 * timezone. The output format is "HH:mm" and it is localized to German
 * ("de-DE").
 * @param dateTime When this parameter is a zoned date-time, the timezone
 * information is ignored and the time is formatted directly. Otherwise, the
 * input is treated as an instant in time and converted to the given timezone
 * before formatting.
 * @param timezone The timezone to format the time in.
 * @returns The formatted time string.
 */
export function formatTime(dateTime: string | Temporal.Instant | Temporal.ZonedDateTime, timezone: string): string {
  const plainTime: Temporal.PlainTime = dateTime instanceof Temporal.ZonedDateTime
    ? dateTime.toPlainTime()
    : Temporal.Instant.from(dateTime)
      .toZonedDateTimeISO(timezone)
      .toPlainTime();
  return plainTime
    .toLocaleString("de-DE", { hour: "2-digit", minute: "2-digit" });
};

export function formatTimeRelative(time: Temporal.PlainTime, now: Temporal.PlainTime): string {
  const deltaMinutes = now.since(time).total("minutes");
  if (deltaMinutes === 0) {
    return "in diesem Moment";
  } else if (deltaMinutes === -1) {
    return "in 1 Minute";
  } else if (deltaMinutes === 1) {
    return "vor 1 Minute";
  } else if (deltaMinutes >= -60 && deltaMinutes < 0) {
    return `in ${Math.round(-deltaMinutes)} Minuten`;
  } else if (deltaMinutes > 0 && deltaMinutes <= 60) {
    return `vor ${Math.round(deltaMinutes)} Minuten`;
  } else {
    return time.toLocaleString("de-DE", { hour: "2-digit", minute: "2-digit" });
  }
};

export function formatDate(date: Temporal.PlainDate | string, timezone: Temporal.TimeZoneLike, opts?: {
  omit_year?: boolean,
  omit_month?: boolean,
  omit_day?: boolean,
  omit_weekday?: boolean,
  no_relative_terms?: boolean,
  now_date?: Temporal.PlainDate
}): string {
  opts = opts || {};
  const plain_date = Temporal.PlainDate.from(date);
  const now = opts.now_date || Temporal.Now.zonedDateTimeISO(timezone).toPlainDate();
  if (!opts.no_relative_terms) {
    if (plain_date.equals(now)) {
      return "Heute";
    } else if (plain_date.equals(now.add({ days: 1 }))) {
      return "Morgen";
    } else if (plain_date.equals(now.subtract({ days: 1 }))) {
      return "Gestern";
    }
  }

  if (plain_date.year === now.year) {
    opts.omit_year = true;
  }

  return plain_date.toLocaleString("de", {
    weekday: opts.omit_weekday ? undefined : "short",
    day: opts.omit_day ? undefined : "2-digit",
    month: opts.omit_month ? undefined : "short",
    year: opts.omit_year ? undefined : "numeric",
  });
}

export function formatDateTime(isoString: string | Temporal.Instant, timezone: string): string {
  const instant = Temporal.Instant.from(isoString);
  const zoned = instant.toZonedDateTimeISO(timezone);
  const date = zoned.toPlainDate();
  const now = Temporal.Now.zonedDateTimeISO(timezone);
  const nowDate = now.toPlainDate();
  if (nowDate.equals(date)) {
    return formatTimeRelative(zoned.toPlainTime(), now.toPlainTime());
  }
  const dateString = formatDate(date, timezone, { now_date: nowDate });
  const timeString = formatTime(instant, timezone);
  return `${dateString}, ${timeString}`;
}

export function formatDateTimeRange(startIso: string | Temporal.Instant, endIso: string | Temporal.Instant, timezone: string): string {
  const start = Temporal.Instant.from(startIso).toZonedDateTimeISO(timezone);
  const end = Temporal.Instant.from(endIso).toZonedDateTimeISO(timezone);
  const startDate = start.toPlainDate();
  const endDate = end.toPlainDate();
  if (startDate.equals(endDate)) {
    return `${formatDate(startDate, timezone)}, ${formatTime(start, timezone)} - ${formatTime(end, timezone)} Uhr`;
  } else if (start.year !== end.year || start.month !== end.month) {
    return `${formatDate(startDate, timezone, { omit_weekday: true })}, ${formatTime(start, timezone)} Uhr - ${formatDate(endDate, timezone, { omit_weekday: true })}, ${formatTime(end, timezone)} Uhr`;
  } else {
    const startDateOpts = { omit_weekday: true, omit_year: true, omit_month: true, no_relative_terms: true };
    return `${formatDate(startDate, timezone, startDateOpts)}. - ${formatDate(endDate, timezone, { no_relative_terms: true, omit_weekday: true })}, ${formatTime(start, timezone)} - ${formatTime(end, timezone)} Uhr`;
  }
}

export function formatDuration(duration: Temporal.Duration): string {
  const duration_all_sec = Math.round(duration.total("seconds"));
  const duration_all_min = Math.floor(duration_all_sec / 60);
  const duration_hour = Math.floor(duration_all_min / 60);
  const duration_min = duration_all_min % 60;
  const duration_sec = duration_all_sec % 60;
  let duration_string = "";
  if (duration_hour !== 0) duration_string += `${duration_hour}h`;
  if (duration_min !== 0) duration_string += ` ${duration_min}min`;
  if (duration_hour === 0 && duration_sec !== 0) duration_string += ` ${duration_sec}s`;
  return duration_string.trim();
}


/** Converts 'xx:yy' to milliseconds since 00:00 */
export function time_string_to_ms(time_string: string): number {
  const [hours, minutes] = time_string.split(":").map(Number);
  return (hours * 3600 + minutes * 60) * 1000;
}

export function to_timestamp(iso_string: Temporal.Instant | string): number {
  return Temporal.Instant.from(iso_string).epochMilliseconds;
}

export function toZonedDate(iso_string: string | Temporal.Instant, timezone: string): Temporal.PlainDate {
  return Temporal.Instant.from(iso_string).toZonedDateTimeISO(timezone).toPlainDate();
}

export function format_time_range(start_iso: string, end_iso: string, timezone: string): string {
  const start = Temporal.Instant.from(start_iso);
  const end = Temporal.Instant.from(end_iso);

  const start_zoned = start.toZonedDateTimeISO(timezone);
  const end_zoned = end.toZonedDateTimeISO(timezone);

  const time_str = (zdt: Temporal.ZonedDateTime) =>
    `${String(zdt.hour).padStart(2, "0")}:${String(zdt.minute).padStart(2, "0")}`;

  const duration = formatDuration(start.until(end));
  const duration_part = duration ? ` (ca. ${duration})` : "";

  return `${time_str(start_zoned)} – ${time_str(end_zoned)}${duration_part}`;
}
